import { Label } from './chunk-676CI6I4.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { useFormContext, FormProvider, Controller } from 'react-hook-form';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var Form = FormProvider;
var FormFieldContext = React.createContext(null);
function FormField(props) {
  return /* @__PURE__ */ jsx(FormFieldContext.Provider, { value: { name: props.name }, children: /* @__PURE__ */ jsx(Controller, { ...props }) });
}
var FormItemContext = React.createContext(null);
function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const form = useFormContext();
  if (!itemContext) throw new Error("useFormField must be used within <FormItem>");
  const fieldError = fieldContext && form ? form.getFieldState(fieldContext.name, form.formState).error : void 0;
  return {
    id: itemContext.id,
    name: fieldContext?.name ?? "",
    formItemId: `${itemContext.id}-form-item`,
    formDescriptionId: `${itemContext.id}-form-item-description`,
    formMessageId: `${itemContext.id}-form-item-message`,
    error: fieldError,
    message: fieldError?.message ?? itemContext.error
  };
}
var FormItem = React.forwardRef(
  function FormItem2({ className, error, ...props }, ref) {
    const id = React.useId();
    const value = React.useMemo(() => ({ id, error }), [id, error]);
    return /* @__PURE__ */ jsx(FormItemContext.Provider, { value, children: /* @__PURE__ */ jsx("div", { ref, className: cn("min-w-0 space-y-2", className), ...props }) });
  }
);
var FormLabel = React.forwardRef(function FormLabel2({ className, ...props }, ref) {
  const { message, formItemId } = useFormField();
  return /* @__PURE__ */ jsx(Label, { ref, htmlFor: formItemId, className: cn(message && "text-destructive", className), ...props });
});
var FormControl = React.forwardRef(function FormControl2(props, ref) {
  const { message, formItemId, formDescriptionId, formMessageId } = useFormField();
  return /* @__PURE__ */ jsx(
    Slot,
    {
      ref,
      id: formItemId,
      "aria-describedby": message ? `${formDescriptionId} ${formMessageId}` : formDescriptionId,
      "aria-invalid": message ? true : void 0,
      ...props
    }
  );
});
var FormDescription = React.forwardRef(
  function FormDescription2({ className, ...props }, ref) {
    const { formDescriptionId } = useFormField();
    return /* @__PURE__ */ jsx("p", { ref, id: formDescriptionId, className: cn("text-sm text-muted-foreground", className), ...props });
  }
);
var FormMessage = React.forwardRef(
  function FormMessage2({ className, children, ...props }, ref) {
    const { message, formMessageId } = useFormField();
    const body = message ?? children;
    if (!body) return null;
    return /* @__PURE__ */ jsx("p", { ref, id: formMessageId, className: cn("text-sm font-medium text-destructive", className), ...props, children: body });
  }
);
var FormRootError = React.forwardRef(
  function FormRootError2({ className, ...props }, ref) {
    const form = useFormContext();
    const message = form?.formState.errors.root?.message;
    if (!message) return null;
    return /* @__PURE__ */ jsx("p", { ref, role: "alert", className: cn("text-sm font-medium text-destructive", className), ...props, children: String(message) });
  }
);
function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
  labelClassName,
  orientation = "vertical",
  reverse = false,
  rowClassName,
  ownsControl = false
}) {
  const labelNode = label ? /* @__PURE__ */ jsxs(FormLabel, { className: labelClassName, children: [
    label,
    required ? /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "ml-0.5 text-destructive", children: "*" }) : null
  ] }) : null;
  const control = ownsControl ? children : /* @__PURE__ */ jsx(FormControl, { "aria-required": required || void 0, children });
  return /* @__PURE__ */ jsxs(FormItem, { error, className: cn("space-y-1.5", className), children: [
    orientation === "horizontal" ? /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", rowClassName), children: [
      reverse ? control : labelNode,
      reverse ? labelNode : control
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      labelNode,
      control
    ] }),
    hint ? /* @__PURE__ */ jsx(FormDescription, { children: hint }) : null,
    /* @__PURE__ */ jsx(FormMessage, {})
  ] });
}

export { Field, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, FormRootError, useFormField };
//# sourceMappingURL=chunk-ID27SN4R.js.map
//# sourceMappingURL=chunk-ID27SN4R.js.map